import { useEffect, useRef } from 'react'
import { AlertCircle, Receipt } from 'lucide-react'

import { Modal } from '@/componentes/ui/Modal'
import { plataCentavos } from '@/lib/formato'
import { useAyudaDe } from '@/componentes/ayuda/useAyudaDe'
import type { CobroDirecto, MedioPagoPos } from './tipos'
import { useCobroSinDetalle } from './cobroSinDetalle'
import { useEnterConfirma } from './useEnterConfirma'
import {
  AvisoReferencia,
  BotonesMedio,
  CampoComprobante,
  CampoReferencia,
  CobroDividido,
  MontoTipeado,
  PanelEntregado,
  TecladoMonto,
} from './PiezasCobro'

interface Props {
  medios: MedioPagoPos[]
  onCobrar: (datos: CobroDirecto) => void
  onCerrar: () => void
  cobrando: boolean
  error?: string | null

  /** Si se muestra el campo del comprobante. Lo decide el local (§1.1). */
  pideComprobante: boolean
}

// Cobro SIN DETALLE, como diálogo del POS normal (F3).
//
// Es para el local que carga la mercadería pero de vez en cuando cobra un
// ticket que ya emitió otro equipo. El local que SOLO cobra así no usa este
// diálogo: tiene la pantalla entera (`CajaSoloCobro`, parámetro
// `pos.solo_cobro`), porque abrir un diálogo en cada venta es un paso de más
// en la operación del día.
//
// Las reglas —el pago implícito, la referencia que traba el cobro, el vuelto,
// la confirmación— viven en `useCobroSinDetalle` y las piezas en
// `PiezasCobro`: los dos layouts comparten todo eso a propósito, o el día que
// una regla cambie va a cambiar en uno nada más.
export function DialogoCobroDirecto({
  medios,
  onCobrar,
  onCerrar,
  cobrando,
  error,
  pideComprobante,
}: Props) {
  useAyudaDe('pos.cobro_directo')

  const refMonto = useRef<HTMLInputElement>(null)
  const c = useCobroSinDetalle({ medios, onCobrar, cobrando })

  // El foco arranca en el monto, que es lo primero que se carga.
  useEffect(() => {
    refMonto.current?.focus()
  }, [])

  // Enter registra, desde donde esté el foco. Con la confirmación abierta el
  // Enter es DE ELLA.
  useEnterConfirma(c.cobrar, { activo: !c.confirmando })

  return (
    <Modal
      onCerrar={onCerrar}
      titulo="Cobrar sin detalle"
      ancho="lg"
      // En el PIE, por lo mismo que en el diálogo de cobro: adentro del cuerpo
      // el botón que registra la plata quedaba abajo del pliegue en un
      // teléfono, y hay que scrollear el diálogo entero para llegar.
      pie={
        <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:items-center">
          <button type="button" onClick={onCerrar} className="btn-secundario">
            Cancelar
          </button>

          <div className="hidden flex-1 sm:block" />

          <button
            type="button"
            onClick={() => void c.cobrar()}
            disabled={!c.listo || cobrando}
            className="btn-primario py-3.5 text-[16px] sm:min-w-[220px]"
          >
            {cobrando ? 'Registrando…' : `Registrar ${plataCentavos(c.montoCentavos)}`}
          </button>
        </div>
      }
    >
      <div className="grid gap-5 md:grid-cols-[1fr_320px]">
        {/* Izquierda: el monto y el teclado */}
        <div className="space-y-4">
          <p className="flex items-start gap-2 rounded-lg bg-crema-alt px-3 py-2.5 text-[13px] text-tinta-suave">
            <Receipt className="mt-0.5 h-4 w-4 shrink-0 text-tinta-tenue" />
            Esta venta va SIN renglones: no queda qué se vendió, solo cuánto entró y con qué
            medio. Suma al turno, al arqueo y al reporte de medios de pago.
          </p>

          {/* Ver la pantalla de solo cobro: el teclado carga una cosa a la vez
              y el display dice cuál. */}
          <MontoTipeado
            texto={c.tecleandoTexto}
            etiqueta={c.tecleandoEtiqueta}
            contexto={
              c.modo === 'dividido' ? (
                <>
                  Total del ticket{' '}
                  <strong className="num text-tinta">{plataCentavos(c.montoCentavos)}</strong>
                  {' · falta '}
                  <strong className="num text-rojo">{plataCentavos(c.falta)}</strong>
                </>
              ) : undefined
            }
            refInput={refMonto}
          />

          <TecladoMonto tecla={c.tecla} />

          {/* El campo lo decide el local: donde la balanza imprime una etiqueta
              sin numerar, es un renglón que el cajero saltea en cada cobro. Ver
              el parámetro `pos.cobro_directo_comprobante`. */}
          {pideComprobante && (
            <CampoComprobante
              id="comprobante-externo"
              valor={c.comprobante}
              onCambio={c.setComprobante}
            />
          )}
        </div>

        {/* Derecha: medios y confirmación */}
        <div className="flex flex-col gap-3">
          <BotonesMedio
            cobrables={c.cobrables}
            medio={c.medio}
            onElegir={c.elegirMedio}
          />

          {c.medio?.pideReferencia && (
            <CampoReferencia
              id="referencia-cobro-directo"
              valor={c.referencia}
              onCambio={c.setReferencia}
            />
          )}

          {c.faltaReferencia && <AvisoReferencia medio={c.medio} />}

          {c.modo === 'dividido' ? (
            <CobroDividido
              pagos={c.pagos}
              onQuitar={c.quitar}
              onAgregar={c.agregar}
              onVolver={c.volverAUnMedio}
              puedeAgregar={c.puedeAgregar}
              motivoNoAgregar={c.motivoNoAgregar}
              importeAAgregar={c.importeAAgregar}
              pagoCentavos={c.pagoCentavos}
              falta={c.falta}
              medio={c.medio}
            />
          ) : (
            <div className="rounded-xl border border-borde bg-papel p-4">
              <p className="text-[13px] text-tinta-tenue">
                {c.implicito
                  ? `Se cobra todo con ${c.implicito.medio.nombre}.`
                  : 'Poné el monto y elegí el medio.'}
              </p>

              {c.montoCentavos > 0 && c.cobrables.length > 1 && (
                <button
                  type="button"
                  onClick={c.dividir}
                  className="mt-2 text-[12.5px] text-tinta-tenue underline decoration-borde-fuerte underline-offset-2 hover:text-verde-oscuro"
                >
                  Dividir en dos o más medios de pago
                </button>
              )}

              {c.montoCentavos > 0 && (
                <div className="mt-3 flex items-baseline justify-between gap-3 border-t border-borde pt-3">
                  <span className="text-[13px] uppercase tracking-wide text-verde-oscuro">
                    Cobrado
                  </span>

                  <span className="num text-[22px] font-bold text-verde-oscuro">
                    {plataCentavos(c.montoCentavos)}
                  </span>
                </div>
              )}
            </div>
          )}

          {c.efectivo && (
            <PanelEntregado
              id="entregado-cobro-directo"
              entregadoTexto={c.entregadoTexto}
              montoCentavos={c.montoCentavos}
              entregado={c.entregado}
              onCambio={c.setEntregado}
              vuelto={c.vuelto}
            />
          )}

          {error && (
            <p className="flex items-start gap-2 rounded-lg bg-rojo/10 px-3 py-2.5 text-[13px] text-rojo">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              {error}
            </p>
          )}
        </div>
      </div>
    </Modal>
  )
}
